import { defaultNavigation } from './navigation';
import { NavigationItem } from './navigation.model';

/** Một module trên rail (POS, SM, WM, FIN…) cùng nhóm rail chứa nó (COM, SCM, FIN…). */
export interface IAppModule {
  /** Mã module — id của mục rail, ví dụ `SM`, `WM`. Đây là giá trị ghi vào sổ lỗi. */
  id: string;
  /** Khoá i18n tên module. */
  title: string;
  /** Nhãn ngắn của nhóm rail, ví dụ `COM`. */
  group: string;
}

interface IPrefixEntry {
  prefix: string;
  module: IAppModule;
}

/** Mọi `link` trong cây con của một mục — module có thể chỉ khai link ở các mục con. */
function collectLinks(item: NavigationItem, out: string[]): void {
  if (item.link && !item.externalLink) out.push(item.link);
  for (const child of item.children ?? []) collectLinks(child, out);
}

/**
 * Bảng tiền tố route → module, dựng MỘT lần từ `navigation.ts` — nguồn duy nhất của rail.
 * Xếp tiền tố dài trước để `/sales/delivery` thắng `/sales`.
 */
function buildPrefixTable(nav: NavigationItem[]): IPrefixEntry[] {
  const table: IPrefixEntry[] = [];
  for (const group of nav) {
    if (group.type !== 'group' || !group.shortTitle) continue;
    for (const item of group.children ?? []) {
      if (!item.id || item.type === 'divider') continue;
      const module: IAppModule = { id: item.id, title: item.title ?? item.id, group: group.shortTitle };
      const links: string[] = [];
      collectLinks(item, links);
      for (const link of links) table.push({ prefix: link.replace(/\/+$/, ''), module });
    }
  }
  return table.sort((a, b) => b.prefix.length - a.prefix.length);
}

const DEFAULT_TABLE = buildPrefixTable(defaultNavigation);

/** Bỏ query + fragment. */
export function stripRouteQuery(url: string | null | undefined): string {
  return (url ?? '').split('#')[0].split('?')[0];
}

/**
 * Module rail của một route FE. Khớp theo RANH GIỚI đoạn đường dẫn: `/sales` khớp `/sales/order`
 * nhưng không khớp `/salesforce`. Không khớp gì ⇒ null.
 */
export function resolveAppModule(
  url: string | null | undefined,
  nav?: NavigationItem[],
): IAppModule | null {
  const path = stripRouteQuery(url);
  if (!path.startsWith('/')) return null;
  const table = nav ? buildPrefixTable(nav) : DEFAULT_TABLE;
  for (const { prefix, module } of table) {
    if (!prefix) continue;
    if (path === prefix || path.startsWith(prefix + '/')) return module;
  }
  return null;
}

/** Tra module theo mã đã ghi trong sổ lỗi (ví dụ `SM`). */
export function findAppModuleById(id: string | null | undefined): IAppModule | null {
  if (!id) return null;
  return DEFAULT_TABLE.find((e) => e.module.id === id)?.module ?? null;
}

/** Danh sách module rail theo thứ tự rail — nguồn cho bộ lọc "Phân hệ". */
export function listAppModules(): IAppModule[] {
  const seen = new Set<string>();
  const out: IAppModule[] = [];
  for (const group of defaultNavigation) {
    if (group.type !== 'group' || !group.shortTitle) continue;
    for (const item of group.children ?? []) {
      if (!item.id || item.type === 'divider' || seen.has(item.id)) continue;
      seen.add(item.id);
      out.push({ id: item.id, title: item.title ?? item.id, group: group.shortTitle });
    }
  }
  return out;
}
